/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { checkYieldAgainstDestination, isExpressionYieldType, rowYield } from '@expression-yield';
import { systemDefaultForParamRow } from '@auto-populated-params';
import { describeUnknownPath, expressionStructureIssues, rootCalculatedPath } from './expression-structure';
import { authoredFloorLiteral, describeFloorBelowBound, type FloorBound } from '../../../utils/floor-bound';
import {
  checkDestinationReference,
  checkDestinationValue,
  isRequiredHere,
  readStoredImportance,
  seedValueFor,
  type DestinationField,
  type DestinationFieldType,
  type FieldImportance,
} from '@destination-field';
import { canonicalizeContextPath } from '@context-roots';
import { foldMappingExpression, isMacroToken, readMappingValueSlots } from '@mapping-value-slots';
import { normalizeWireRendering, type WireRendering } from '@wire-rendering';
import {
  originOf,
  readValueOrigin,
  revertValueOf,
  type DetectionConfidence,
  type ValueOrigin,
} from '@value-provenance';
import type { MacroMode } from '../inputs/MacroAwareValueInput';
import type { MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import {
  matchMacroName,
  getSchemaSuggestions,
  isKnownContextPath,
  suggestedMacroFor,
} from '../inputs/data-model-schema';
import type { MacroExpression } from '../../../types/decorator-expression';
import type { DemandSourceMethod } from '../../../types';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import type { MappingIssue } from './mapping-issues';
import {
  getMissingParamFields,
  paramCatalogEntry,
  AUTO_POPULATED_PARAM_NAMES,
  type ParamCatalogField,
} from './param-field-catalog';
import { isResolvableMacro } from './resolvable-macros';
import { summarizeMacroExpression, type ExpressionNameLookup } from './expression-summary';
import { formatStaticValue } from './ortb-static-value';
import { storedText } from '../../../utils/stored-text';
import {
  describeArtifactMetadataOnWire,
  describeRequiredClaim,
  describeRequiredRisk,
  isAnswered,
  slotsDefault,
  describeRowSlots,
  declaresDefault,
  expressionDefault,
  isPlainPath,
  leafFromRowSlots,
  rowSlotsFromLeaf,
  withExpressionDefault,
  type RequiredMark,
  type RowMappingKind,
  type RowValueCapabilities,
} from './mapping-value-round-trip';

export interface DemandSourceParamField {
  /** The omission answer, and only that, since `X46` — omit when unresolved, or send `foo=`. */
  optional: boolean;
  /**
   * **How much the destination wants a value here** (`X46`) — an authoring claim that alters
   * nothing on the wire. Read off a pre-split row's `optional: false` by `readStoredImportance`.
   */
  importance?: FieldImportance;
  suppress?: boolean;
  /** The literal is a credential (`#1371`), stored encrypted and never read back. */
  secret?: true;
  /** The authored literal, and nothing else, since `N11f` (`#1177`). */
  defaultValue?: string | number;
  /** The macro token as authored, delimiters included — `{ip}`, `{{IP}}`, `[timestamp]`. */
  macro?: string;
  /** What to send when the macro, path or expression resolves to nothing. */
  default?: string;
  /** Pre-`#1216` spelling of `default` (`X3`), read off a stored row. */
  fallback?: string;
  /** Derived since `N11f`, not a disambiguator — the slots say which answer a row carries. */
  type?: 'static' | 'dynamic';
  /** The type this destination takes, as the row carries it (`FD3a`) — seeded by a vendor template; grades only. */
  valueType?: DestinationFieldType;
  description?: string;
  mappingSource?: string;
  /**
   * **`false` only** — the operator declining the driver's own value when this row resolves to
   * nothing (`X33a`, `#1251`). Absence means *accept*, which is what the driver does.
   */
  acceptSystemDefault?: false;
  /** Structured decorator expression (overrides defaultValue/mappingSource when present). */
  expression?: MacroExpression;
  /**
   * **Send as** — how a structured value goes out on this row (D23 · R3).
   *
   * Absent means omit-and-report, and that is the *default rather than a gap*: an author
   * who has chosen nothing has decided nothing, and inventing a flattening on their
   * behalf is the same act as inventing a supply chain node.
   *
   * The editor must show the **rendered result**, not just the choice — that is what
   * keeps this from becoming another invisible transformation, and it is the same
   * discipline as the resolution mark.
   */
  sendAs?: WireRendering;

  /**
   * How this row came to be (R4/R4b). Absent means the operator set it — there is
   * deliberately no `user` member, because chipping everything makes the chips
   * invisible.
   *
   * Server-owned: the console renders it, never derives it. A label computed here
   * could not detect a second storage path writing the row, which is the class of
   * bug #891 was.
   */
  provenance?: ValueOrigin;
}

/**
 * **Imported, not mirrored** (D23 · R3, #1083). This was a hand-kept copy with a drift
 * guard comparing the two member lists; the console now aliases `@wire-rendering` and the
 * guard is retired, because there is nothing left to drift.
 *
 * The reason it earns an alias where every other shared shape is mirrored: R3 requires the
 * editor to show the **rendered result**, so the console needs `renderForWire` itself, not
 * just the vocabulary — and a second implementation would preview something the driver
 * does not send. A wrong preview is worse than none (#1002).
 *
 * Re-exported so importers of this module keep their import path.
 */
export type { WireRendering } from '@wire-rendering';

/**
 * **Imported, not mirrored, since `#1001`** (`@value-provenance`). This was a hand-kept copy of the
 * union; `L3`'s record brought a legacy read and a confirm rule with it, and a copy of either would be
 * a second reader of one stored shape. Re-exported so importers of this module keep their import path.
 */
export {
  confirmedOrigin,
  isInferredDeclaration,
  isUnconfirmedGuess,
  templateSuppliedOrigin,
  isValueProvenance,
  originOf,
  readValueOrigin,
  revertValueOf,
  type DetectionConfidence,
  type SampleLiteral,
  type ValueOrigin,
  type ValueProvenance,
} from '@value-provenance';

export interface DemandSourceParamsDto {
  fields?: Record<string, DemandSourceParamField>;
}

/**
 * Extended mode type for parameter rows.
 * - 'static'      – fixed string value; maps to `StaticExpression`
 * - 'macro'       – backward-compatible alias for simple path-mapped parameters.
 *                   New rows should prefer 'calculated'; existing rows using
 *                   'macro' continue to work via the `mappingSource` field.
 * - 'calculated'  – resolved from the request context via a dot-notation path;
 *                   stores a full `CalculatedExpression` in the `expression` field
 * - 'computed'    – combines multiple operands via concat/arithmetic;
 *                   stores a full `ComputedExpression` in the `expression` field
 * - 'conditional' – if/then/else expression over a context condition;
 *                   stores a full `ConditionalExpression` in the `expression` field
 */
export type ParameterMode = MacroMode | 'calculated' | 'computed' | 'conditional';

export interface ParameterRow {
  id: string;
  name: string;
  mode: ParameterMode;
  /** The authored literal. Empty on a row that resolves from somewhere (`N11f`). */
  value: string;
  /**
   * **What to send when a row with no expression resolves to nothing** — a row that is only a name
   * (the seeded `maxPodDuration` with 180), or a round override that changes only the default. A row
   * with an expression keeps its default **on the expression** (`X58`), so this is never beside one.
   */
  default?: string;
  /**
   * **The omission answer, and only that, since `X46`** — omit when nothing resolves, or send
   * `foo=`? It is the one claim on this row that changes what goes on the wire.
   */
  optional: boolean;
  /**
   * **Let the driver's own value stand when this row resolves to nothing** (`X33a`, `#1251`).
   *
   * Only present where the operator **declined** it. Absence means *accept*, which is what the
   * driver does and what every row stored before this was authored under — writing `true` would put
   * a value on the wire model that changes nothing and invite somebody to compare against it.
   */
  acceptSystemDefault?: false;
  /**
   * **How much this destination wants a value here** (`X46`) — a claim about the *field*, which
   * alters nothing on the wire and decides only what the console tells the operator.
   *
   * It was `!optional` until 2026-09-02, which meant an operator choosing *omit it rather than
   * send `pubid=`* silently switched off the Prove phase's warning that the id was blank. Seeded
   * from the catalogue at creation ({@link rowFromCatalogField}) and read from a pre-`X46` row's
   * `optional: false` at the store boundary ({@link paramsDtoToRows}); absent means nobody stated
   * one, which is **not** `'optional'`.
   */
  importance?: FieldImportance;
  /**
   * **The type this destination takes, as the row carries it** (`FD3a`, `1f-iii`). A vendor template
   * seeds it; the checker grades the row against it ahead of our catalog. Grades only: a query string
   * carries text, so the Type control stays refused and shows it as declared.
   */
  valueType?: DestinationFieldType;
  suppress?: boolean;
  /**
   * **Whether this row's literal is a credential** (`CR2`, `#1371`). `false` is an operator's untick of
   * a suggestion, held for the visit; only `true` is stored.
   */
  secret?: boolean;
  description: string;
  /**
   * **Everything that is not a literal** (`X58`, `#1428`): a path, a `{macro}` (a `macro` node), a
   * calculation, a decision. Serialised to `DemandSourceParamField.expression`, the one form a row
   * stores, and it carries its own default.
   */
  expression?: MacroExpression;
  /**
   * How this row came to be (R4/R4b). Server-owned — carried through the editor so
   * a round-trip preserves it, never invented here. Absent means operator-set.
   *
   * A record since `#1001`: a detected mapping carries its detector's confidence and what Revert
   * returns to, both cleared when the operator opens the row and applies it (`confirmedOrigin`).
   */
  provenance?: ValueOrigin;

  /**
   * **Send as** (D23 · R3, #1083) — how a structured value goes out on this row.
   *
   * Absent means omit-and-report, and that is a *default rather than a gap*: an author who
   * has chosen nothing has decided nothing. Narrowed through `normalizeWireRendering` on
   * read, which **rejects rather than repairs** — a malformed stored value must not degrade
   * into a different rendering.
   */
  sendAs?: WireRendering;
}

/**
 * Default macro + mapping per standard parameter name. A **lookup table**, not a row
 * source — `getMacroDefaultForParam` reads it to fill in a row the operator is already
 * editing, so it carries no provenance of its own. Rows built from the catalog come
 * from `rowFromCatalogField`, which marks them `template`.
 *
 * **The paths must be the ones `MACRO_DEFAULTS` declares.** This table carried the
 * pre-#882 spelling — `session.device.*`, `session.privacy.*`, `session.video.*` —
 * for months after #882 corrected `MACRO_DEFAULTS` and wrote down why: those
 * branches match no node in `SessionMetadataDto`, so a `calculated` expression
 * walking one resolves to its default every time. They survived here because the
 * `{macro}` in the same row rescued the value, which makes the row's stated mapping
 * a lie that nothing on the wire reveals. See #998.
 */
export const STANDARD_FIELD_DEFAULTS: Array<Omit<ParameterRowInput, 'id' | 'provenance'> & { name: string }> = [
  {
    name: 'ip',
    mode: 'macro',
    value: '',
    macro: '{ip}',
    // **`importance` beside `optional`, or the row means one thing before a save and another
    // after** (`X46`). These two literals were written when `optional: false` *was* the
    // required-ness claim; a row created from one has no `importance`, so it renders unmarked —
    // until it is saved and read back, where `readStoredImportance` reads the `false` as
    // `required` and the mark appears. Stating it here is what makes the two readings agree.
    importance: 'required',
    optional: false,
    description: 'User IP address',
    mappingSource: 'session.user.ipAddress',
  },
  {
    name: 'ua',
    mode: 'macro',
    value: '',
    macro: '{ua}',
    importance: 'required',
    optional: false,
    description: 'User agent string',
    mappingSource: 'session.user.userAgent',
  },
  {
    name: 'cb',
    mode: 'macro',
    value: '',
    macro: '{cb}',
    optional: true,
    description: 'Cache buster',
    mappingSource: '',
  },
  {
    name: 'gdpr',
    mode: 'macro',
    value: '',
    macro: '{gdpr}',
    optional: true,
    description: 'GDPR applies flag',
    mappingSource: 'session.user.gdpr',
  },
  {
    name: 'gdpr_consent',
    mode: 'macro',
    value: '',
    macro: '{gdpr_consent}',
    optional: true,
    description: 'GDPR consent string',
    mappingSource: 'session.user.gdprConsent',
  },
  {
    name: 'us_privacy',
    mode: 'macro',
    value: '',
    macro: '{us_privacy}',
    optional: true,
    description: 'CCPA privacy string',
    mappingSource: 'session.user.usPrivacy',
  },
  {
    name: 'dnt',
    mode: 'macro',
    value: '',
    macro: '{dnt}',
    optional: true,
    description: 'Do Not Track flag',
    mappingSource: 'session.user.dnt',
  },
  {
    name: 'lmt',
    mode: 'macro',
    value: '',
    macro: '{lmt}',
    optional: true,
    description: 'Limit Ad Tracking flag',
    mappingSource: 'session.user.lmt',
  },
  {
    name: 'w',
    mode: 'macro',
    value: '',
    macro: '{w}',
    optional: true,
    description: 'Player width',
    mappingSource: 'session.context.playerWidth',
  },
  {
    name: 'h',
    mode: 'macro',
    value: '',
    macro: '{h}',
    optional: true,
    description: 'Player height',
    mappingSource: 'session.context.playerHeight',
  },
  {
    name: 'url',
    mode: 'macro',
    value: '',
    macro: '{url}',
    optional: true,
    description: 'Page URL',
    mappingSource: 'session.context.pageUrl',
  },
  {
    name: 'dur',
    mode: 'macro',
    value: '',
    macro: '{dur}',
    optional: true,
    description: 'Maximum ad duration in seconds',
    mappingSource: 'session.content.contentLength',
  },
];

export const PARAMETER_SUGGESTIONS: MappingSuggestion[] = [
  { value: 'session.user.ipAddress', category: 'session' },
  { value: '{ip}', category: 'macro' },
  { value: '{ua}', category: 'macro' },
  { value: '{gdpr}', category: 'macro' },
  { value: '{gdpr_consent}', category: 'macro' },
  { value: '{us_privacy}', category: 'macro' },
  { value: '{dnt}', category: 'macro' },
  { value: '{lmt}', category: 'macro' },
  { value: '{w}', category: 'macro' },
  { value: '{h}', category: 'macro' },
  { value: '{url}', category: 'macro' },
  { value: '{dur}', category: 'macro' },
  { value: '{cb}', category: 'macro' },
];

/**
 * **The one place a row's value slots are split** (`N11f`, `#1177`).
 *
 * Every row in the console is built here — the catalogs, the presets, a pasted tag, the
 * wizard, a stored document — and every one of them wrote the macro token into `value`,
 * because until the split that is where it lived. Splitting *here* rather than at each caller
 * is what stops the next builder putting it back: it reads the row exactly as the driver reads
 * the stored document, through the same function.
 */
function paramRowSlots(partial?: ParameterRowInput): Pick<ParameterRow, 'value' | 'expression' | 'default'> {
  const resolvesFromSomewhere =
    !!partial?.macro || !!partial?.mappingSource || !!partial?.expression || isMacroToken(partial?.value);
  const slots = readMappingValueSlots({
    value: partial?.value,
    macro: partial?.macro,
    default: partial?.default,
    mappingSource: partial?.mappingSource,
    expression: partial?.expression,
    // `mode` is not consulted: it is derived from the slots, and a caller that has one has
    // already told us what the row holds by what it put in it.
    type: resolvesFromSomewhere ? undefined : 'static',
  });
  // **The parameter store's legacy read** (`X58`, `#1428`): a macro or a typed path is the expression
  // it is. **Canonicalised where the value enters the row** (`#1213`) — an alias is an input spelling,
  // and `session.user.deviceType` is what the engine resolves.
  const folded = foldMappingExpression({
    macro: slots.macro,
    mappingSource: canonicalizeContextPath(partial?.mappingSource ?? ''),
    expression: partial?.expression,
    default: slots.default,
  }) as MacroExpression | undefined;
  const expression = withRowDefault(folded, partial?.expression ? slots.default : undefined);
  return {
    value: slots.literal !== undefined ? String(slots.literal) : '',
    expression,
    // A row with no expression keeps its default on the row; one with an expression, on the expression.
    ...(expression ? {} : { default: slots.default }),
  };
}

/**
 * **A row default, moved onto the expression that declares a slot for it and has none** (`X58`).
 * Exported because the header row and the leaf fold need the same rule.
 */
export function withRowDefault(
  expression: MacroExpression | undefined,
  rowDefault: string | undefined,
): MacroExpression | undefined {
  const declares = expression && declaresDefault(expression);
  if (!declares || !rowDefault || expressionDefault(declares) !== undefined) return expression;
  return withExpressionDefault(declares, rowDefault) as MacroExpression;
}

/** What a row's `mode` is, read from what it holds (`N11f`: the slots say it, not a marker). */
function modeOf(expression: MacroExpression | undefined): ParameterMode {
  if (!expression) return 'static';
  if (expression.type === 'conditional') return 'conditional';
  if (expression.type === 'computed') return 'computed';
  if (expression.type === 'macro' || isPlainPath(expression)) return 'macro';
  return 'calculated';
}

/**
 * **What a row is built from** — a row, or the slots a document written before `X58` stored: a
 * `macro`, a typed `mappingSource`, a row-level `default`. {@link createParameterRow} reads them into
 * the row's one expression, so a caller may say either and no row carries both.
 */
export type ParameterRowInput = Partial<ParameterRow> & { macro?: string; mappingSource?: string; default?: string };

export function createParameterRow(partial?: ParameterRowInput): ParameterRow {
  const slots = paramRowSlots(partial);
  return {
    id: partial?.id ?? `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: partial?.name ?? '',
    mode: modeOf(slots.expression),
    ...slots,
    optional: partial?.optional ?? true,
    importance: partial?.importance,
    ...(partial?.valueType !== undefined ? { valueType: partial.valueType } : {}),
    suppress: partial?.suppress ?? false,
    ...(partial?.secret !== undefined ? { secret: partial.secret } : {}),
    acceptSystemDefault: partial?.acceptSystemDefault,
    description: partial?.description ?? '',
    provenance: partial?.provenance,
    sendAs: partial?.sendAs,
  };
}

/**
 * **A detected parameter row with its guess taken back** (`#1001`).
 *
 * The row becomes what the sample actually carried — a static value — and keeps everything about it
 * that is the row's rather than the guess's: its name, its claims about the destination, its note.
 * It stays `detected`, because the literal is still something we read out of the sample, and it
 * carries no confidence, because a literal is not a guess.
 *
 * A row with nothing to revert to is returned unchanged; the mark has already said why.
 */
export function revertDetectedRow(row: ParameterRow): ParameterRow {
  const value = revertValueOf(row.provenance);
  if (value === undefined) return row;
  return createParameterRow({
    id: row.id,
    name: row.name,
    mode: 'static',
    value: String(value),
    optional: row.optional,
    importance: row.importance,
    valueType: row.valueType,
    suppress: row.suppress,
    secret: row.secret,
    acceptSystemDefault: row.acceptSystemDefault,
    description: row.description,
    provenance: originOf('detected'),
    sendAs: row.sendAs,
  });
}

function normalizeParamName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * The macro and context path to pre-fill a parameter row of this name with.
 *
 * **The token is the macro that matched, never the parameter's own name.** This
 * previously returned `` `{${paramName}}` `` for a *non-exact* match, which mints a macro
 * out of whatever the operator happened to call the field: `description_url` matches on
 * the token `url` and was handed `{description_url}`, which no vocabulary has ever
 * resolved. That put two of the ten names #1090 had just deleted straight back into the
 * wizard — and unlike the six offer *lists*, this is a **generator**, so any name carrying
 * `url`/`ip`/`ua`/`os`/`w`/`h` as a token could produce one. `matchMacroName` already
 * returns the macro precisely so a caller does not have to guess (#1003, #1114).
 *
 * A non-exact match still answers for a name an **operator** typed — `player_width` finding
 * `w` is the case #1003 warns against tightening away. It must never decide what a
 * **vendor's** field means: `AD_SERVER_PRESETS.parameterRules.fieldMap` declares that, one
 * entry at a time, after the guess here mapped GAM's `description_url` to the page URL
 * because it contains the token `url` (2026-08-20).
 */
export function getMacroDefaultForParam(
  paramName: string,
): { macro?: string; defaultMappingSource?: string; confidence: DetectionConfidence } | null {
  const target = normalizeParamName(paramName);
  const match = STANDARD_FIELD_DEFAULTS.find((item) => normalizeParamName(item.name) === target);
  if (!match) {
    const macro = matchMacroName(paramName);
    if (!macro) return null;
    return {
      macro: `{${macro.macro}}`,
      defaultMappingSource: macro.path,
      // **The grade the matcher already returns** (`#1001`). `matchMacroName` says whether it matched
      // the whole name or a token of it, and this dropped the answer — so `content_language` finding
      // `{language}` on one word reached the row looking exactly like `ip` finding `{ip}`.
      confidence: macro.exact ? 'high' : 'medium',
    };
  }
  // **`macro`, not `defaultValue`** (`N11f`): what this returns has always been a macro token,
  // and calling it a default is what let it be written into the slot the row's *default* now
  // owns. One name per concept, and the rename is what makes the two callers say which slot
  // they mean.
  return {
    macro: match.macro,
    defaultMappingSource: match.mappingSource,
    confidence: 'high',
  };
}

export function getParameterSuggestions(query: string): MappingSuggestion[] {
  const schemaSuggestions = getSchemaSuggestions(query);
  if (!query.trim()) {
    return [...schemaSuggestions, ...PARAMETER_SUGGESTIONS];
  }

  const q = query.trim().toLowerCase();
  const legacyMatches = PARAMETER_SUGGESTIONS.filter((item) => item.value.toLowerCase().includes(q));

  const merged = [...schemaSuggestions, ...legacyMatches];
  const seen = new Set<string>();
  return merged.filter((item) => {
    const key = `${item.value}:${item.category ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isMacroLike(value: string): boolean {
  return /^[[{].*[\]}]$/.test(value) || /\{[a-z_]+\}/i.test(value);
}

function parseParamsInput(
  input?: Record<string, unknown> | DemandSourceParamsDto | string,
): Record<string, unknown> | DemandSourceParamsDto | null {
  if (!input) return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return input;
}

/**
 * Collapse rows that share a name (case-insensitively), keeping the first. A legacy array shape can
 * carry one name twice; without this the editor shows a duplicate and deleting one leaves the other
 * (the parameter "comes back" on reopen).
 */
function dedupeRowsByName(rows: ParameterRow[]): ParameterRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = row.name.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Dedupe, then drop driver CONFIGURATION so it never appears as a request
 * parameter row.
 *
 * `version` and `timeout` are read by the drivers, never emitted on the wire,
 * and are authored by first-class controls — Protocol Version on the wizard's
 * Connection step, Timeout as its own field. Listing them here too gave one
 * value two authoring surfaces. The server folds a row by either name onto the
 * field (`foldRetiredStaticParams`, `#1135`), so a row could not persist anyway.
 */
function finalizeRows(rows: ParameterRow[]): ParameterRow[] {
  return dedupeRowsByName(rows).filter((row) => !DRIVER_CONFIG_PARAM_NAMES.has(row.name.trim().toLowerCase()));
}

/**
 * Names that are driver CONFIGURATION, not request parameters — each has a first-class field on the
 * source (`protocolVersion`, `timeout`). The mirror of `RETIRED_STATIC_CONFIG_KEYS` in `libs/dto`, which
 * is what the server folds a row by either name onto.
 */
export const DRIVER_CONFIG_PARAM_NAMES: ReadonlySet<string> = new Set(['version', 'timeout']);

/**
 * The protocol version a source declares — the ONE reader for this value, shared by the wizard's
 * Connection step, the detail page header, validation and the test diagnosis.
 *
 * `DemandSourceDto.protocolVersion` since `#1135`. It lived in `params.staticParams.version` with a
 * `fields.version` leak read as a fallback; the server folds both onto the field on every write and
 * `R6` repairs what is at rest, so there is one place to read.
 */
export function extractProtocolVersion(
  source: { protocolVersion?: unknown } | null | undefined,
  fallback: string,
): string {
  const value = source?.protocolVersion;
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
}

export function paramsDtoToRows(input?: Record<string, unknown> | DemandSourceParamsDto): ParameterRow[] {
  const parsedInput = parseParamsInput(input);
  if (!parsedInput || typeof parsedInput !== 'object') return [];

  const rows: ParameterRow[] = [];

  const pushArrayRows = (items: Array<Record<string, unknown>>) => {
    items.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const name = storedText(item.name).trim();
      if (!name) return;
      const rawValue = formatStaticValue(item.value);
      const itemType = storedText(item.type).toLowerCase();
      const mode: MacroMode =
        itemType === 'macro' || itemType === 'dynamic' || isMacroLike(rawValue) ? 'macro' : 'static';

      rows.push(
        createParameterRow({
          name,
          mode,
          value: rawValue,
          optional: item.optional !== false,
          acceptSystemDefault: item.acceptSystemDefault === false ? false : undefined,
          // **The `X46` legacy read, at the store boundary and nowhere else.** A pre-split row said
          // *required* by saying `optional: false`; from here on the two are separate slots.
          importance: readStoredImportance(item as { importance?: FieldImportance; optional?: boolean }),
          suppress: item.suppress === true,
          ...(item.secret === true ? { secret: true } : {}),
          description: storedText(item.description),
          mappingSource: storedText(item.mappingSource),
          provenance: readValueOrigin(item.provenance),
          sendAs: normalizeWireRendering(item.sendAs),
        }),
      );
    });
  };

  if (Array.isArray(parsedInput)) {
    parsedInput.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const row = item as Record<string, unknown>;
      const name = storedText(row.name).trim();
      if (!name) return;
      const rawValue = formatStaticValue(row.value);
      rows.push(
        createParameterRow({
          name,
          mode:
            storedText(row.type).toLowerCase() === 'macro' ||
            storedText(row.type).toLowerCase() === 'dynamic' ||
            isMacroLike(rawValue)
              ? 'macro'
              : 'static',
          value: rawValue,
          optional: row.optional !== false,
          acceptSystemDefault: row.acceptSystemDefault === false ? false : undefined,
          importance: readStoredImportance(row as { importance?: FieldImportance; optional?: boolean }),
          suppress: row.suppress === true,
          ...(row.secret === true ? { secret: true } : {}),
          description: storedText(row.description),
          mappingSource: storedText(row.mappingSource),
          provenance: readValueOrigin(row.provenance),
          sendAs: normalizeWireRendering(row.sendAs),
        }),
      );
    });
    return finalizeRows(rows);
  }

  const rawFields = (parsedInput as Record<string, unknown>).fields;
  if (Array.isArray(rawFields)) {
    pushArrayRows(rawFields as Array<Record<string, unknown>>);
  } else if (rawFields && typeof rawFields === 'object') {
    Object.entries(rawFields as Record<string, unknown>).forEach(([name, fieldRaw]) => {
      const field = fieldRaw;
      if (field == null || typeof field !== 'object' || Array.isArray(field)) {
        const primitive = Array.isArray(field) ? field.join(',') : storedText(field);
        rows.push(
          createParameterRow({
            name,
            mode: isMacroLike(primitive) ? 'macro' : 'static',
            value: primitive,
            optional: true,
            description: '',
            mappingSource: '',
          }),
        );
        return;
      }

      const normalizedField = field as Partial<DemandSourceParamField>;
      const storedExpr = normalizedField.expression;

      /**
       * **The stored row, split by the same function the driver splits it with** (`N11f`).
       *
       * The console used to read `defaultValue` as the row's *value*, so `{ defaultValue: 180 }`
       * — the shape on every seeded source — rendered as a static `180` while the driver
       * consulted it only after context resolution found nothing. The row said one thing and
       * the wire did another, which is the disagreement this whole slice is about; reading
       * through `readMappingValueSlots` makes the editor show what is actually sent.
       */
      const slots = readMappingValueSlots({
        value: normalizedField.defaultValue,
        macro: normalizedField.macro,
        default: normalizedField.default,
        fallback: normalizedField.fallback,
        mappingSource: normalizedField.mappingSource,
        expression: storedExpr,
        type: normalizedField.type,
      });

      // If the field carries a structured expression, restore the appropriate mode.
      let mappedMode: ParameterMode;
      if (storedExpr) {
        if (storedExpr.type === 'conditional') mappedMode = 'conditional';
        else if (storedExpr.type === 'computed') mappedMode = 'computed';
        else mappedMode = 'calculated';
      } else {
        mappedMode = slots.literal !== undefined ? 'static' : 'macro';
      }

      rows.push(
        createParameterRow({
          name,
          mode: mappedMode,
          value: slots.literal !== undefined ? String(slots.literal) : '',
          macro: slots.macro,
          default: slots.default,
          optional: normalizedField.optional !== false,
          // **The canonical stored shape**, so this is the read that matters (`X33a`). Only `false`
          // is stored, and only `false` is read back: absence is *accept*, not *unstated*.
          acceptSystemDefault: normalizedField.acceptSystemDefault === false ? false : undefined,
          // **The `X46` legacy read** — this is the canonical stored shape, so it is the one that
          // matters: a pre-split field said *required* by saying `optional: false`.
          importance: readStoredImportance(normalizedField),
          ...(isExpressionYieldType(normalizedField.valueType) ? { valueType: normalizedField.valueType } : {}),
          suppress: normalizedField.suppress === true,
          ...(normalizedField.secret === true ? { secret: true } : {}),
          description: normalizedField.description ?? '',
          mappingSource: normalizedField.mappingSource ?? '',
          expression: storedExpr,
          provenance: readValueOrigin(normalizedField.provenance),
          sendAs: normalizeWireRendering(normalizedField.sendAs),
        }),
      );
    });
  }
  // If the input carried the canonical structure — even an EMPTY one — return whatever it produced
  // (possibly zero rows). Falling through to the flat-object fallback below would misread the
  // structure keys themselves as parameter NAMES — rows with `[object Object]` values — and saving
  // them would double-wrap and corrupt the params. `staticParams` is still recognised as a structure
  // key for that reason alone: the bag is retired (`#1135`) and the server folds it on write, so a
  // document that still carries one is never read as a parameter named `staticParams`.
  const carriesRetiredBag = (parsedInput as Record<string, unknown>).staticParams !== undefined;
  if (rows.length > 0 || rawFields !== undefined || carriesRetiredBag) return finalizeRows(rows);

  // Truly flat legacy shape (`{ip:'{ip}', ua:'{ua}'}`) — one param per top-level key.
  return finalizeRows(
    Object.entries(parsedInput).map(([name, rawValue]) =>
      createParameterRow({
        name,
        mode: isMacroLike(String(rawValue)) ? 'macro' : 'static',
        value: rawValue != null ? String(rawValue) : '',
        optional: true,
        description: '',
        mappingSource: '',
      }),
    ),
  );
}

/**
 * A row's value slots as the document stores them (`N11f`) — **written, never inferred**.
 *
 * The reason the split is worth its cost is here: what used to be one key with three meanings
 * is now three keys with one each, so nothing downstream has to guess which it was handed.
 * `readMappingValueSlots` exists for documents written before this ran.
 */
function storedValueSlots(row: ParameterRow): Pick<DemandSourceParamField, 'defaultValue' | 'default'> {
  return { defaultValue: row.value || undefined, default: row.default || undefined };
}

export function rowsToParamsDto(rows: ParameterRow[]): DemandSourceParamsDto {
  const fields: Record<string, DemandSourceParamField> = {};

  rows.forEach((row) => {
    const name = row.name.trim();
    if (!name) return;

    // **A row writes one form** (`X58`, `#1428`): an expression, which carries its own default, or a
    // literal. No `macro`, `mappingSource` or row-level `default` is written any more.
    if (row.expression) {
      fields[name] = {
        optional: row.optional,
        importance: row.importance,
        ...(row.valueType ? { valueType: row.valueType } : {}),
        suppress: row.suppress,
        ...(row.secret ? { secret: true as const } : {}),
        acceptSystemDefault: row.acceptSystemDefault,
        type: 'dynamic',
        description: row.description || undefined,
        expression: row.expression,
        provenance: row.provenance,
        sendAs: row.sendAs,
      };
      return;
    }

    // Driver configuration (protocol version / request timeout) is authored on its own field and is
    // never a request parameter (`#1135`), so a row by either name is not written.
    if (DRIVER_CONFIG_PARAM_NAMES.has(name.toLowerCase())) return;

    // A static request parameter → `fields`.
    fields[name] = {
      optional: row.optional,
      importance: row.importance,
      ...(row.valueType ? { valueType: row.valueType } : {}),
      suppress: row.suppress,
      ...(row.secret ? { secret: true as const } : {}),
      acceptSystemDefault: row.acceptSystemDefault,
      ...storedValueSlots(row),
      type: 'static',
      description: row.description || undefined,
      provenance: row.provenance,
      sendAs: row.sendAs,
    };
  });

  return { fields };
}

// ---------------------------------------------------------------------------
// Row ↔ MappingLeaf (the rebuilt editor authors each row's value via the shared
// MappingControl, which speaks MappingLeaf). #851.
// ---------------------------------------------------------------------------

/** Known macro names (from the shared vocabulary) — for unresolved-macro warnings. */
/**
 * What the **driver** can resolve — not `MACRO_DEFAULTS`, which is the much smaller
 * catalog of *suggested* mappings. Judging a 98-name vocabulary with a 38-name
 * editorial list is what warned that `{app_store_url}` was unknown while the driver
 * resolved it fine, and it did that to roughly two thirds of valid macros.
 */

/**
 * A parameter row **absorbs a description**: a plain path's note lands in the row's own field, so the
 * row and the path do not each carry one. See `RowValueCapabilities`.
 */
const PARAM_ROW_SLOTS: RowValueCapabilities = { absorbsDescription: true };

/** Project a row's value mapping into the MappingLeaf the shared control edits. */
export function rowToLeaf(row: ParameterRow): MappingLeaf {
  return {
    ...leafFromRowSlots([row.name], {
      value: row.value ?? '',
      expression: row.expression,
      description: row.description,
    }),
  };
}

/** Fold an edited MappingLeaf back into a row (name/optional/suppress preserved). */
export function applyLeafToRow(row: ParameterRow, leaf: MappingLeaf): ParameterRow {
  const slots = rowSlotsFromLeaf(leaf, PARAM_ROW_SLOTS);
  // A row default meets an expression the moment the row gains one, and moves onto it.
  const expression = withRowDefault(slots.expression, row.default);
  return {
    ...row,
    value: slots.value,
    expression,
    default: expression ? undefined : row.default,
    mode: modeOf(expression),
    ...(slots.description !== undefined ? { description: slots.description } : {}),
  };
}

export type { RowMappingKind };

/** A one-line summary of a row's current mapping — for the collapsed row chip. */
export function describeRowMapping(
  row: ParameterRow,
  /**
   * **Names a shared expression the row addresses** (`XS5`, `#1066`). Optional: with no lookup the
   * summary reads *shared expression*, which is true and says nothing it cannot support — the case
   * the required-and-nullable rule exempts.
   */
  nameFor?: ExpressionNameLookup,
): { label: string; kind: RowMappingKind } {
  return describeRowSlots({ value: row.value ?? '', expression: row.expression }, (expression) =>
    summarizeMacroExpression(expression, nameFor),
  );
}

/**
 * **What a row's `required` declaration means, on a surface where it means something**
 * (`N11g`'s visible half, `#1177`; Todd, 2026-08-26: *"do we add a tag/chip/UI treatment for
 * optional on the item itself so it's clear?"*).
 *
 * Four things decide the wording, and each comes from something already on that row:
 *
 * - **It marks `required`, never `optional`.** `createParameterRow` defaults `optional: true`, so
 *   optional is the majority — and a mark on the majority is noise, which is how a useful mark
 *   stops being read. `required` is also the row carrying a consequence: it is the one **sent
 *   empty** (`foo=`) rather than omitted when nothing resolves, which is the surprising outcome
 *   and is today invisible until the row is expanded.
 * - **It never restates the resolution mark.** `→ omitted` is *observed*; this is *declared*. Two
 *   surfaces for one fact is how `#999` and `#1002` happened. If they ever disagree that is a
 *   finding, not a rendering choice.
 * - **Required + empty is the coverage claim, and it reads by the artifact's kind.** On a template
 *   an unfilled required field *is* the shape — `TemplatesPage` already says **You supply this**,
 *   reused here rather than phrased a second time. On an instance it is unfinished, and
 *   {@link validateParams} already warns; the badge must not become a second warning
 *   (guidance pattern 16 — one warnings taxonomy).
 * - **A suppressed row is not marked at all.** `suppress` and `required` are near-opposites —
 *   *never send* against *always send, even empty* — and they share `MappingRow`'s one `state`
 *   slot, so a contradictory pair is unrenderable by construction.
 *
 * **The projection itself is {@link describeRequiredClaim}**, shared with the ORTB body leaf, which
 * has the same claim and a **different consequence**: an unresolved body key is omitted (#880),
 * never sent empty. This is the parameter row's reading of it.
 *
 * @param isTemplate whether the artifact being edited is a template. `null` where the surface
 *   does not know — a row with no answer then says only that it is required, which is true on
 *   either reading.
 */
export function describeRequiredMark(row: ParameterRow, isTemplate: boolean | null): RequiredMark | null {
  return describeRequiredClaim(
    {
      // **`importance`, not `!optional`** (`X46`). The two were one boolean until 2026-09-02, so
      // this badge moved when an operator answered a question about the wire.
      required: isRequiredHere(row.importance) && !row.suppress,
      answered: isAnswered(row),
      // **The row's own omission answer, not a constant** (`X46`). It was hardcoded `sendEmpty`,
      // which was true only while *required* meant `!optional`. A required row that is also set to
      // be omitted when unresolved is now representable — and it is exactly the case the tooltip
      // must not lie about.
      whenUnresolved: row.optional ? 'omit' : 'sendEmpty',
    },
    isTemplate,
  );
}

/**
 * Validate the parameter rows into the shared warnings model (guidance §16).
 * VAST-first: unresolved macros, empty values, duplicates, config keys authored
 * as params, and missing required catalog fields (fixable by adding them).
 */
/** The parameter names the ORTB driver writes to every `imp.bidfloor` (`applyKnownFieldValue`). */
const ORTB_FLOOR_ROW_NAMES: ReadonlySet<string> = new Set(['bidfloor', 'floor']);

export function validateParams(
  rows: ParameterRow[],
  method: DemandSourceMethod | undefined,
  /**
   * **What the source's terms do to an authored floor** (`#918`), so a `bidfloor` row the driver will
   * raise says so. Required and nullable (`#1134`): a surface that does not know the source's terms
   * passes `null` and says it.
   */
  floor: FloorBound | null,
  /**
   * **Names what a row addresses, so the shape check can read a shared expression's declaration**
   * (`4e`). A parameter row cannot store a `Type` of its own — a query string is text — so the only
   * thing that can say a row's value is a list or an object is the expression it uses. Required and
   * nullable for `validateOrtbBody`'s reason (`#1254`): optional, three of four callers would forget.
   */
  expressionLookup: ExpressionNameLookup | null,
): MappingIssue[] {
  const issues: MappingIssue[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const name = row.name.trim();
    if (!name) continue;
    const lower = name.toLowerCase();

    if (seen.has(lower)) {
      issues.push({ kind: 'conflict', message: `Duplicate parameter "${name}" — the last one wins.`, ref: name });
    }
    seen.add(lower);

    if (lower === 'version' || lower === 'timeout') {
      issues.push({
        kind: 'wrong-place',
        message: `"${name}" is driver configuration, not a request parameter — set it on the Connection step.`,
        ref: name,
      });
      continue;
    }

    // Same rule as the header validator (#998): a path the model does not have resolves to nothing on
    // every request. The root's path is this surface's to check; `[*]` there is the expression walk's
    // (`expressionStructureIssues`), now that a typed path is an expression like any other (`X58`).
    const rootPath = rootCalculatedPath(row.expression);
    if (rootPath && !rootPath.includes('[*]') && !isKnownContextPath(rootPath)) {
      issues.push({ kind: 'unresolved', message: describeUnknownPath(`"${name}"`, rootPath), ref: name });
    }

    // Metadata about the demand source, going on the wire (`#1322`). The sentence is shared.
    for (const message of describeArtifactMetadataOnWire(row, `"${name}"`)) {
      issues.push({ kind: 'metadata-on-wire', message, ref: name });
    }

    /**
     * **Does the literal match what this parameter accepts?** (`#1191`, `#914` tier 1.)
     *
     * Read through `readMappingValueSlots`, not off `row.value`: one row has four answer slots and
     * the stored `value` is not always the one on the wire (`N11f`). The **default** is checked
     * too — `#914` asks for it and gives the reason: a badly-formed default is worse than a
     * badly-formed value, because it only fires when the mapping resolves to nothing and nobody
     * is watching.
     *
     * `error` rather than `warn`, and it does not block: `R3a`'s tie-break is *if we are not sure,
     * it is error, not block* (`#1193`), and a declared type is a claim about somebody else's
     * system, so the operator may know something the catalog does not.
     *
     * **The row's own declaration first** (`FD3a`, `1f-iii`): a vendor template stamps `valueType` on a
     * field our catalog does not know, and that is the only declaration such a field has.
     */
    const catalogField = withRowValueType(paramCatalogEntry(method, name), name, row.valueType);
    const rowDefault = slotsDefault({ expression: row.expression }) ?? row.default;
    const macroName = row.expression?.type === 'macro' ? row.expression.name : null;
    for (const literal of [row.expression ? undefined : row.value || undefined, rowDefault]) {
      const finding = checkDestinationValue(catalogField, literal, 'text');
      // The finding's own level — see the note at the ORTB body's value sweep (`D41`).
      if (finding) issues.push({ kind: finding.kind, message: finding.message, ref: name });
    }

    /**
     * **And what the row points at, against what the parameter says it carries** (`4d`, `#1413`,
     * `FD11`). The check above grades a **literal** and exempts everything else, because *a macro
     * is not gradeable until it resolves* — true of a value and false of a reference. Eighteen of
     * these parameters declare the concept they carry, so `us_privacy` mapped to the viewer's IP
     * address is checkable with nothing new declared.
     *
     * The path is read from `mappingSource` **or** the expression's root, because a row that
     * cannot collapse to a bare path keeps both (`rowSlotsFromLeaf`) and a row stored by another
     * writer may keep only the second. A `warn` (Todd, 2026-09-22), and nothing blocks a save.
     */
    const reference = checkDestinationReference(catalogField, {
      macro: macroName ? `{${macroName}}` : null,
      path: rootCalculatedPath(row.expression),
    });
    if (reference) issues.push({ kind: reference.kind, message: reference.message, ref: name });

    /**
     * **Shape against shape** (`#1413` piece 3, `4e`) — a shared expression declaring a list or an
     * object, on a parameter that declares a single value. *Send structured values as* is offered
     * here, so the redirect names it; and one element of it, where the row's value is a path.
     */
    if (row.expression) {
      const lookup = expressionLookup ? (ref: string) => expressionLookup(ref)?.expression : null;
      const target = row.expression.type === 'reference' ? expressionLookup?.(row.expression.ref) : undefined;
      const shape = checkYieldAgainstDestination(catalogField, rowYield(undefined, row.expression, lookup), {
        from: target?.name ?? null,
        path: rootCalculatedPath(row.expression),
        offersRendering: true,
        rendered: row.sendAs !== undefined,
        // A query string is text: a list goes out comma-joined, so only an object is a finding (`#1458`).
        textSurface: true,
      });
      if (shape) issues.push({ kind: shape.kind, message: shape.message, ref: name });
    }

    // **A floor row the source's terms will raise** (`#918`). ORTB only: the driver writes this row
    // to every `imp.bidfloor` and bounds it after (`applyKnownFieldValue`, `applyBidFloorBounds`),
    // where VAST sends it as written.
    if (method === 'ORTB' && ORTB_FLOOR_ROW_NAMES.has(name.toLowerCase())) {
      for (const literal of [row.expression ? undefined : row.value || undefined, rowDefault]) {
        const authored = authoredFloorLiteral(literal);
        const message = authored === null ? null : describeFloorBelowBound(name, authored, floor);
        if (message) issues.push({ kind: 'overridden', message, ref: name });
      }
    }

    /**
     * **Required, and not guaranteed to arrive** (`X46`, Todd 2026-09-02). Warning, never a block —
     * the operator may know something the catalogue does not. It reads the *slots*, so a `{cb}` row
     * with an empty `value` counts as derived and answered, and it reads the default through
     * `slotsDefault`, so a default authored on an expression counts exactly as one authored on the
     * row. A literal, or a derived value with a default, draws nothing.
     */
    const requiredRisk = describeRequiredRisk(
      {
        required: isRequiredHere(row.importance),
        suppressed: row.suppress === true,
        derived: !!row.expression,
        // **An accepted system default is a default** (Todd, 2026-09-24). A row overriding one of the
        // parameters sent automatically, with *Accept system default* ticked, falls back to our value
        // when it resolves to nothing, so *no default* was false there. It warned on `ip` and `ua` as
        // they arrive from a template, which is the same value the driver would send unasked. It asks
        // the function the editor's checkbox asks, so the warning and the control cannot disagree.
        hasDefault:
          (rowDefault ?? '').trim() !== '' ||
          (row.acceptSystemDefault !== false &&
            systemDefaultForParamRow({ name, expression: row.expression }) !== undefined),
        // The surface's own spelling of absence, from this row's own omission answer.
        whenUnresolved: row.optional ? 'omit' : 'sendEmpty',
      },
      `"${name}"`,
    );
    if (requiredRisk) issues.push({ kind: 'at-risk', message: requiredRisk, ref: name });

    // What is unfinished **inside** the row's expression (`4c`, `#1334`).
    issues.push(...expressionStructureIssues(row.expression, `"${name}"`, name, 'row'));

    const summary = describeRowMapping(row);
    if (summary.kind === 'empty') {
      issues.push({ kind: 'missing-value', message: `"${name}" has no value and won't be sent.`, ref: name });
    } else if (summary.kind === 'macro') {
      const token = macroName;
      if (token && !isResolvableMacro(token)) {
        issues.push({
          kind: 'unresolved',
          // An unresolved VAST parameter is omitted when optional and sent empty
          // when required — it is NOT sent literally. That is the header rule, and
          // this message was copied from the header validator with it. The three
          // surfaces deliberately differ (CLAUDE.md, macro resolution semantics).
          message: `"${name}" uses an unknown macro {${token}} that won't resolve — the parameter will be ${
            row.optional === false ? 'sent empty' : 'omitted'
          }.`,
          ref: name,
        });
      }
    }
  }

  /**
   * Required catalog fields that are **genuinely** absent — fixable by adding them
   * (VAST only; the ORTB catalog is empty, so nothing is demanded here — guidance §14).
   *
   * An auto-populated field is not absent. It was still warned about after R5 landed,
   * which put a warning saying *"Required parameter `app_bundle` isn't set"* directly
   * below a panel listing `app_bundle` as sent automatically — the screen contradicting
   * itself, and "Fix all" offering to add rows for values already going out.
   *
   * This is the #1013 shape one surface over: a check that was correct about its own
   * scope (*"no row names this"*) and wrong about the question being asked (*"is it
   * sent?"*).
   */
  for (const field of getMissingParamFields(method, seen)) {
    if (field.importance !== 'required') continue;
    if (AUTO_POPULATED_PARAM_NAMES.has(field.name)) continue;
    issues.push({
      kind: 'missing-value',
      message: `Required parameter "${field.name}" (${field.description}) isn't set.`,
      ref: field.name,
      fixable: true,
    });
  }

  return issues;
}

/**
 * Build a pre-mapped row from a catalog field (for Add recommended / Fix).
 *
 * `template` (R4/R4b). The click authored the **inclusion**; the catalog authored
 * the **value** — `field.defaultMacro` was composed for the protocol, not for this
 * source — and provenance describes the value.
 *
 * The argument for leaving it unchipped is that the operator pressed the button and
 * knows where it came from. That holds only at the moment of authoring. Later, or
 * for a second editor, an unchipped row claims deliberate setup for this source,
 * which over-states an intent that never existed and leaves "is this safe to
 * remove?" unanswerable. `template` answers it: standard field, so removing it is a
 * question about the protocol rather than about somebody's reason.
 */
export function rowFromCatalogField(field: ParamCatalogField): ParameterRow {
  return createParameterRow({
    name: field.name,
    mode: 'macro',
    // `D32`: what a newly added row starts with. Every VAST entry is `derived`, so this is its
    // macro — an `operator`-supplied field seeds **empty**, which is the honest starting state
    // rather than an invented value.
    value: seedValueFor(field) ?? '',
    // **Two claims, seeded together and independent afterwards** (`X46`). `importance` is what the
    // destination said; `optional` is the wire answer that a required field conventionally wants —
    // present-but-empty rather than absent, which is the distinction a query string can express and
    // some exchanges act on. Seeding both keeps the wire exactly as it was while making either
    // editable without moving the other.
    importance: field.importance,
    optional: field.importance !== 'required',
    description: field.description,
    provenance: originOf('template'),
  });
}

/**
 * The row a parameter name should produce when it is added by hand (#922 · 2).
 *
 * Guidance §5 is *"pre-map added fields, never leave a blank"*, and it held for
 * `+ Add recommended` and for the ValidationSummary's Fix while adding a parameter by
 * name produced an unmapped static row — so the rule looked like a property of one
 * button rather than of the surface. This is the shared answer, so all three paths add
 * the same row for the same name.
 *
 * Precedence, and both entries are an **existing** answer rather than a new classifier:
 *
 * 1. **The catalog**, via {@link rowFromCatalogField} — an authored default with a
 *    description and an importance, which is strictly more than a macro match knows.
 * 2. **{@link matchMacroName}**, *the* name→macro matcher (#1003). Whole-token only:
 *    a substring rule mapped `position` to the OS string and `content_language` to the
 *    user agent, and every one of those shipped a plausible value.
 *
 * A name neither recognises gets a **static** row, deliberately. That is a real "we have
 * no opinion", not a blank we could have filled — and guessing here would put the #1003
 * defect back through a different door.
 */
export function rowForParamName(name: string, method: DemandSourceMethod | undefined): ParameterRow {
  const trimmed = name.trim();
  const catalogEntry = paramCatalogEntry(method, trimmed);
  if (catalogEntry) return rowFromCatalogField(catalogEntry);

  // The canonical name, not whichever alias matched: seeding is an offer, and the
  // same one-name-per-concept rule applies to it as to the picker.
  const macro = suggestedMacroFor(trimmed);
  if (macro) {
    return createParameterRow({
      name: trimmed,
      mode: 'macro',
      value: `{${macro.macro}}`,
      optional: true,
      provenance: originOf('template'),
    });
  }

  return createParameterRow({ name: trimmed, mode: 'static', optional: true });
}

/**
 * Add the catalog row a validation issue names — the one implementation of the
 * parameter editor's **Fix** (R9, #899).
 *
 * Pure, because it now has two callers: the editor's own `ValidationSummary`, and
 * the source readiness panel, which stages the same fix into the section's draft
 * before the editor opens. A second copy would let "Fix" mean two different things
 * depending on which button was pressed — and the panel's copy would be the one
 * nobody re-checked when the catalog changed.
 *
 * Returns `rows` unchanged when the name is not in the catalog, or is already
 * present. The already-present guard matters for the staged path specifically: the
 * panel decides the fix against the **saved** source, and the operator may have
 * added the row by hand between the read and the edit.
 */
export function applyParamFix(
  rows: ParameterRow[],
  name: string,
  method: DemandSourceMethod | undefined,
): ParameterRow[] {
  const field = paramCatalogEntry(method, name);
  if (!field) return rows;
  if (rows.some((row) => row.name.trim().toLowerCase() === field.name)) return rows;
  return [...rows, rowFromCatalogField(field)];
}

/**
 * **One entry in the add-a-parameter picker** (`#1166`).
 *
 * A single shape because the picker renders one list, and the two things in it are genuinely
 * different: a **catalog** field is one of our concepts this protocol carries, and a **template**
 * field is a slot this vendor accepts and nobody else has heard of. The distinction is carried in
 * the data (`row`) rather than in two lists, so the ordering rule below can be stated once.
 */
export interface ParamNameSuggestion {
  name: string;
  description: string;
  /** What the row will map to, for the `→ x` hint. Empty when nothing is declared — `D32`. */
  seed: string;
  importance?: ParamCatalogField['importance'];
  /**
   * **The driver sends this whether or not a row exists.**
   *
   * Marked rather than removed (`#1166` · 3). Hiding it makes the picker disagree with the *Sent
   * automatically* panel three inches below; listing it silently invites a row that changes
   * nothing, which is `#1013`'s shape. Saying so is the only reading that is true on both counts.
   */
  autoSent: boolean;
  /**
   * The template's row, **verbatim**, when this name came from one — the mapping, `sendAs` and
   * coverage claim the template authored. `null` for a catalog field, which has no row yet.
   */
  row: ParameterRow | null;
}

/**
 * **What the name box offers, and in what order** (`#1166`).
 *
 * Todd, testing `#1090`: *"we offer the fields we already include in the 'sent automatically' in the
 * suggested params… and the auto-complete doesn't include the template's optional fields."* The two
 * sets were exactly inverted — the picker listed what happens anyway and omitted what nothing will
 * ever send.
 *
 * So the order is **by what the operator can only get here**:
 *
 * 1. **the template's withheld optional fields** — in no catalog, in no panel, reachable by no other
 *    route than typing the vendor's spelling exactly;
 * 2. **catalog fields the driver does not volunteer** — `us_privacy` and its kind, in catalog order,
 *    which is already most-important-first;
 * 3. **catalog fields sent automatically** — kept and marked, never hidden.
 *
 * The cap is what makes the order load-bearing rather than cosmetic: at eight entries against
 * eighteen catalog fields, *appended* template fields would never be on screen without a query,
 * which is the reported defect surviving into the fix for it.
 *
 * A template name that the catalog also declares is **the template's** — the vendor is the authority
 * on what its own endpoint accepts, and two entries for one name would let the operator add a
 * duplicate row the editor then refuses.
 */
export function paramNameSuggestions(input: {
  /** Catalog fields not already configured — `getMissingParamFields`. */
  catalog: ReadonlyArray<ParamCatalogField>;
  /** The applied template's withheld optional rows, or `[]` where no template is in scope. */
  templateOptional: ReadonlyArray<ParameterRow>;
  /** Names already on the source, lower-cased. */
  existingNames: ReadonlySet<string>;
  /** What has been typed so far. */
  query: string;
  limit?: number;
}): ParamNameSuggestion[] {
  const { catalog, templateOptional, existingNames, limit = 8 } = input;
  const query = input.query.trim().toLowerCase();

  const fromTemplate: ParamNameSuggestion[] = [];
  const claimed = new Set<string>();
  for (const row of templateOptional) {
    const key = row.name.trim().toLowerCase();
    if (!key || claimed.has(key) || existingNames.has(key)) continue;
    claimed.add(key);
    fromTemplate.push({
      name: row.name,
      description: row.description,
      // The row already holds what it will send; a macro row shows its macro, a static row its
      // value, and a row the template left empty shows nothing rather than an arrow into a blank.
      seed: String(row.value ?? '').trim(),
      importance: 'optional',
      autoSent: AUTO_POPULATED_PARAM_NAMES.has(key),
      row,
    });
  }

  const fromCatalog: ParamNameSuggestion[] = [];
  for (const field of catalog) {
    const key = field.name.trim().toLowerCase();
    if (claimed.has(key) || existingNames.has(key)) continue;
    fromCatalog.push({
      name: field.name,
      description: field.description ?? '',
      seed: seedValueFor(field) ?? '',
      importance: field.importance,
      autoSent: AUTO_POPULATED_PARAM_NAMES.has(key),
      row: null,
    });
  }

  const ordered = [
    ...fromTemplate,
    ...fromCatalog.filter((entry) => !entry.autoSent),
    ...fromCatalog.filter((entry) => entry.autoSent),
  ];

  return ordered.filter((entry) => !query || entry.name.toLowerCase().includes(query)).slice(0, limit);
}

/**
 * **A destination as this row declares it** (`FD3a`): the row's `valueType`, else the catalog's. The
 * `X46` shape importance already has. A row with no claim leaves the catalog entry as it is, and a
 * field no catalog knows is declared by the row alone.
 */
function withRowValueType(
  catalogField: DestinationField | undefined,
  name: string,
  valueType: DestinationFieldType | undefined,
): GradedDestination | undefined {
  if (!valueType) return catalogField;
  return { ...(catalogField ?? { name }), valueType };
}

/** What the checkers read of a destination — nothing about importance, which a row claims separately. */
type GradedDestination = Pick<DestinationField, 'name' | 'valueType' | 'value' | 'constraint' | 'readAs'>;
